/**
 * Piezas compartidas por las 6 plantillas de PDF del Balance General
 * (Estado de Posicion Financiera).
 *
 * Es un modulo de DATOS: no importa pdfmake ni React, solo calcula. Lo consumen
 * `reporteDeEstadoDePosicionFinancieraPdf.js` (clasico) y
 * `PdfV2.0/reporteDeEstadoDePosicionFinancieraPdfV2.js` (moderno), y el catalogo
 * TIPOS_PDF_BALANCE lo lee tambien el menu de la pantalla del reporte para no
 * tener las claves escritas en dos lados.
 */

import round2Decimals from "../Components/round2Decimals";

// ── Catalogo de plantillas ──────────────────────────────────────────────────
// `moderno` decide el diseno (clasico vs V2); `subtotales` prende los grupos por
// subtipo SAT y el resumen; `analisis` agrega el % de integracion y las razones
// financieras. `analisis` siempre implica `subtotales`.
const SIN_ANALISIS = "El formato de siempre, sin agrupaciones";
const CON_SUBTOTALES = "Agrupa en circulante y fijo, con su subtotal";
const CON_ANALISIS = "Subtotales + un anexo aparte con % y razones";

export const TIPOS_PDF_BALANCE = {
  clasico: {
    clave: "clasico",
    etiqueta: "Clásico",
    etiquetaCorta: "Sin análisis",
    descripcion: SIN_ANALISIS,
    moderno: false,
    subtotales: false,
    analisis: false,
  },
  clasicoSubtotales: {
    clave: "clasicoSubtotales",
    etiqueta: "Clásico con subtotales",
    etiquetaCorta: "Con subtotales",
    descripcion: CON_SUBTOTALES,
    moderno: false,
    subtotales: true,
    analisis: false,
  },
  clasicoAnalisis: {
    clave: "clasicoAnalisis",
    etiqueta: "Clásico con análisis completo",
    etiquetaCorta: "Con análisis completo",
    descripcion: CON_ANALISIS,
    moderno: false,
    subtotales: true,
    analisis: true,
  },
  moderno: {
    clave: "moderno",
    etiqueta: "Moderno",
    etiquetaCorta: "Sin análisis",
    descripcion: SIN_ANALISIS,
    moderno: true,
    subtotales: false,
    analisis: false,
  },
  modernoSubtotales: {
    clave: "modernoSubtotales",
    etiqueta: "Moderno con subtotales",
    etiquetaCorta: "Con subtotales",
    descripcion: CON_SUBTOTALES,
    moderno: true,
    subtotales: true,
    analisis: false,
  },
  modernoAnalisis: {
    clave: "modernoAnalisis",
    etiqueta: "Moderno con análisis completo",
    etiquetaCorta: "Con análisis completo",
    descripcion: CON_ANALISIS,
    moderno: true,
    subtotales: true,
    analisis: true,
  },
};

/** Las plantillas de un diseño, en el orden en que se ofrecen en el menú. */
export const opcionesPdfBalance = (moderno) =>
  Object.values(TIPOS_PDF_BALANCE).filter((tipo) => tipo.moderno === moderno);

export const TIPO_PDF_BALANCE_DEFAULT = "clasico";

/** Devuelve la definicion de la plantilla; cae al clasico si la clave no existe. */
export const definicionTipoPdf = (tipo) =>
  TIPOS_PDF_BALANCE[tipo] || TIPOS_PDF_BALANCE[TIPO_PDF_BALANCE_DEFAULT];

export const esModerno = (tipo) => definicionTipoPdf(tipo).moderno;
export const traeSubtotales = (tipo) => definicionTipoPdf(tipo).subtotales;
export const traeAnalisis = (tipo) => definicionTipoPdf(tipo).analisis;

// ── Agrupacion por subtipo del agrupador SAT ────────────────────────────────
// El subtipo lo trae cada cuenta en `agrupadorSat.subtipo` / `.subtipoCodigo`,
// que llegan al PDF porque el controlador hace $lookup + $unwind del agrupador
// completo. "1" es corto plazo (circulante), "2" es largo plazo (fijo).
// Los nombres van en el eje circulante / fijo, que es como se leen en la practica,
// no en el "a corto / a largo plazo" con el que viene el catalogo del SAT.
const TITULOS_SUBTIPO = {
  activo: { 1: "ACTIVO CIRCULANTE", 2: "ACTIVO FIJO" },
  pasivo: { 1: "PASIVO CIRCULANTE", 2: "PASIVO FIJO" },
};

const LLAVE_TOTAL_SUBTIPO = {
  activo: { 1: "activoCirculante", 2: "activoFijo" },
  pasivo: { 1: "pasivoCirculante", 2: "pasivoFijo" },
};

const ETIQUETA_TOTAL_SUBTIPO = {
  activo: { 1: "Total Activo Circulante", 2: "Total Activo Fijo" },
  pasivo: { 1: "Total Pasivo Circulante", 2: "Total Pasivo Fijo" },
};

/**
 * Segmentos con valor del codigo de una cuenta: ["100", "01", "003"].
 *
 * NO se usa `fila.nivel`: el backend lo calcula comparando cada nivel contra ""
 * (reportesFinancieros.controllers.js), pero el modelo CuentaContable declara
 * nivel1..nivel5 con `default: null` y `null !== ""` es true, asi que una cuenta
 * guardada con nulos se reporta como nivel 5. El prefijo del codigo si es
 * confiable porque solo mira que el valor tenga contenido.
 */
const prefijoDe = (fila) => {
  const niveles = [fila.nivel1, fila.nivel2, fila.nivel3, fila.nivel4, fila.nivel5].map(
    (valor) => String(valor === null || valor === undefined ? "" : valor)
  );
  let corte = 0;
  while (corte < niveles.length && niveles[corte] !== "") corte += 1;
  return niveles.slice(0, corte);
};

/** Una cuenta es "mayor" cuando solo tiene el primer nivel con valor. */
const esCuentaMayor = (fila) => prefijoDe(fila).length === 1;

/**
 * Quita las cuentas sin saldo. El backend ya filtra `saldo === 0` exacto, pero
 * el saldo sale de restar cargos contra abonos: un residuo de punto flotante
 * (0.0000000001) sobrevive a ese filtro y se imprime como "$ 0.00". Aqui se
 * compara contra el valor REDONDEADO, que es el que el usuario ve.
 */
export const filtrarSinSaldo = (filas = []) =>
  (filas || []).filter((fila) => fila && round2Decimals(fila.saldo || 0) !== 0);

/**
 * Anota cada cuenta con su `profundidad` (1..5) y con `esPadre`, para que las
 * plantillas dibujen el arbol: sangria por profundidad y mas peso/tinte en los
 * padres.
 *
 * `esPadre` se resuelve contra el renglon SIGUIENTE, no contra la profundidad:
 * las cuentas vienen ordenadas por codigo, asi que los hijos van inmediatamente
 * despues de su padre. Asi una cuenta de nivel 3 se ve como hoja cuando el
 * filtro de nivel corta ahi, y como padre cuando sus hijos si aparecen — que es
 * justo lo que no puede saber la profundidad por si sola.
 *
 * Hay que llamarla DESPUES de filtrarSinSaldo: un padre que se quedo sin hijos
 * visibles es una hoja para efectos del dibujo.
 */
export const marcarJerarquia = (filas = []) => {
  const prefijos = (filas || []).map(prefijoDe);
  return (filas || []).map((fila, indice) => {
    const prefijo = prefijos[indice];
    const siguiente = prefijos[indice + 1];
    const esPadre =
      !!siguiente &&
      siguiente.length > prefijo.length &&
      prefijo.every((segmento, i) => segmento === siguiente[i]);
    return { ...fila, profundidad: prefijo.length || 1, esPadre };
  });
};

/** Sangria de la descripcion, en puntos. El primer nivel no se recorre. */
export const sangriaDe = (fila) => ((fila.profundidad || 1) - 1) * 5;

/**
 * Parte un arreglo plano de cuentas en grupos por subtipo SAT, conservando el
 * orden por codigo dentro de cada grupo.
 *
 * Las cuentas mayores NO entran a ningun grupo: su saldo es el rollup de todo el
 * tipo, asi que meterlas en un subtipo duplicaria el grupo entero. El encabezado
 * del grupo ocupa su lugar.
 *
 * @param {Array} filas  arrayActivo o arrayPasivo tal como llega del backend
 * @param {'activo'|'pasivo'} seccion
 * @returns {Array<{clave,titulo,llaveTotal,etiquetaTotal,filas}>|null}
 *          null cuando no queda nada que agrupar (pasa con el filtro "Nivel 1"),
 *          para que la plantilla caiga al render plano de siempre.
 */
export const agruparPorSubtipo = (filas = [], seccion) => {
  const titulos = TITULOS_SUBTIPO[seccion];
  if (!titulos) return null;

  const candidatas = (filas || []).filter((fila) => fila && !esCuentaMayor(fila));
  if (candidatas.length === 0) return null;

  const grupos = [];
  const porClave = new Map();
  for (const fila of candidatas) {
    // Sin agrupador (o con cualquier otro valor) se asume corto plazo: es donde
    // cae el grueso del catalogo y evita inventar un tercer grupo huerfano.
    const clave = String(fila.agrupadorSat?.subtipoCodigo || "") === "2" ? 2 : 1;
    if (!porClave.has(clave)) {
      const grupo = {
        clave,
        titulo: titulos[clave],
        llaveTotal: LLAVE_TOTAL_SUBTIPO[seccion][clave],
        etiquetaTotal: ETIQUETA_TOTAL_SUBTIPO[seccion][clave],
        filas: [],
      };
      porClave.set(clave, grupo);
      grupos.push(grupo);
    }
    porClave.get(clave).filas.push(fila);
  }

  // El orden natural del balance es circulante antes que fijo y corto antes que
  // largo, aunque el catalogo de cuentas venga numerado al reves.
  grupos.sort((a, b) => a.clave - b.clave);
  return grupos;
};

// ── Analiticas ──────────────────────────────────────────────────────────────
const dividir = (numerador, divisor) =>
  !divisor ? null : round2Decimals(numerador / divisor);

const porcentajeDe = (valor, base) =>
  !base ? null : round2Decimals((valor / base) * 100);

/**
 * Arma subtotales, analisis vertical y razones financieras a partir del bloque
 * `analiticas` que manda el backend (subtotales exactos por subtipo SAT,
 * calculados sobre el saldo propio de cada cuenta).
 *
 * @param {object} values  el payload completo del reporte
 * @returns {{subtotales,porcentajes,razones}|null} null si el backend no mando
 *          `analiticas` (respuesta vieja): las plantillas omiten los bloques en
 *          vez de reventar.
 */
export const construirAnaliticas = (values = {}) => {
  const origen = values.analiticas;
  if (!origen) return null;

  // El backend manda las llaves en el eje del catalogo SAT (corto / largo plazo);
  // de aqui para arriba todo se llama circulante / fijo, que es como se imprime.
  const activoCirculante = round2Decimals(origen.activoCorto || 0);
  const activoFijo = round2Decimals(origen.activoLargo || 0);
  const totalActivo = round2Decimals(activoCirculante + activoFijo);

  const pasivoCirculante = round2Decimals(origen.pasivoCorto || 0);
  const pasivoFijo = round2Decimals(origen.pasivoLargo || 0);
  const totalPasivo = round2Decimals(pasivoCirculante + pasivoFijo);

  const capitalContable = round2Decimals(origen.capitalContable || 0);
  const utilidadEjercicio = round2Decimals(origen.utilidadEjercicio || 0);
  const totalCapital = round2Decimals(capitalContable + utilidadEjercicio);

  const totalPasivoMasCapital = round2Decimals(totalPasivo + totalCapital);
  const inventarios = round2Decimals(origen.inventarios || 0);

  const subtotales = {
    activoCirculante,
    activoFijo,
    totalActivo,
    pasivoCirculante,
    pasivoFijo,
    totalPasivo,
    capitalContable,
    utilidadEjercicio,
    totalCapital,
    totalPasivoMasCapital,
    // Debe dar cero. Si no, el catalogo tiene cuentas mal clasificadas o polizas
    // descuadradas; las plantillas lo pintan en rojo.
    diferencia: round2Decimals(totalActivo - totalPasivoMasCapital),
    inventarios,
  };

  // Analisis vertical: todo contra el Activo Total, que es la base clasica del
  // balance (y por la ecuacion contable tambien es el Pasivo mas Capital).
  const porcentajes = {
    activoCirculante: porcentajeDe(activoCirculante, totalActivo),
    activoFijo: porcentajeDe(activoFijo, totalActivo),
    totalActivo: totalActivo ? 100 : null,
    pasivoCirculante: porcentajeDe(pasivoCirculante, totalActivo),
    pasivoFijo: porcentajeDe(pasivoFijo, totalActivo),
    totalPasivo: porcentajeDe(totalPasivo, totalActivo),
    capitalContable: porcentajeDe(capitalContable, totalActivo),
    utilidadEjercicio: porcentajeDe(utilidadEjercicio, totalActivo),
    totalCapital: porcentajeDe(totalCapital, totalActivo),
    totalPasivoMasCapital: porcentajeDe(totalPasivoMasCapital, totalActivo),
  };

  const razones = {
    capitalDeTrabajo: round2Decimals(activoCirculante - pasivoCirculante),
    razonCirculante: dividir(activoCirculante, pasivoCirculante),
    pruebaDelAcido: dividir(activoCirculante - inventarios, pasivoCirculante),
    endeudamiento: porcentajeDe(totalPasivo, totalActivo),
    apalancamiento: dividir(totalPasivo, totalCapital),
    solvencia: dividir(totalActivo, totalPasivo),
  };

  return { subtotales, porcentajes, razones };
};

// ── Formato ─────────────────────────────────────────────────────────────────
/** "2.98" / "N/A". `dividir` devuelve null cuando el divisor es cero. */
export const formatoVeces = (valor) =>
  valor === null || valor === undefined ? "N/A" : `${round2Decimals(valor).toFixed(2)}`;

/** "61.0%" / "N/A". */
export const formatoPorcentaje = (valor) =>
  valor === null || valor === undefined ? "N/A" : `${round2Decimals(valor).toFixed(1)}%`;

/**
 * Renglones del bloque "Análisis del Balance", en el orden en que se imprimen.
 * `fuerte` marca los totales (negritas + fondo tenue) y `alerta` el descuadre.
 * Lo comparten el clasico y el moderno para que digan exactamente lo mismo.
 */
export const renglonesResumen = (analiticas) => {
  if (!analiticas) return [];
  const { subtotales } = analiticas;
  return [
    { llave: "activoCirculante", etiqueta: "Activo Circulante" },
    { llave: "activoFijo", etiqueta: "Activo Fijo" },
    { llave: "totalActivo", etiqueta: "TOTAL ACTIVO", fuerte: true },
    { llave: "pasivoCirculante", etiqueta: "Pasivo Circulante" },
    { llave: "pasivoFijo", etiqueta: "Pasivo Fijo" },
    { llave: "totalPasivo", etiqueta: "TOTAL PASIVO", fuerte: true },
    { llave: "capitalContable", etiqueta: "Capital Contable" },
    { llave: "utilidadEjercicio", etiqueta: "Utilidad del Ejercicio" },
    { llave: "totalCapital", etiqueta: "TOTAL CAPITAL", fuerte: true },
    {
      llave: "totalPasivoMasCapital",
      etiqueta: "TOTAL PASIVO + CAPITAL",
      fuerte: true,
    },
    {
      llave: "diferencia",
      etiqueta: "Diferencia",
      fuerte: true,
      alerta: subtotales.diferencia !== 0,
    },
  ];
};

/**
 * Renglones del bloque "Razones Financieras". `formato` dice como pintar el
 * valor: "dinero" lo formatea el llamador con dineroDisabledMask, "veces" y
 * "porcentaje" usan los helpers de arriba.
 */
export const renglonesRazones = (analiticas) => {
  if (!analiticas) return [];
  const { razones } = analiticas;
  return [
    {
      etiqueta: "Capital de trabajo",
      valor: razones.capitalDeTrabajo,
      formato: "dinero",
      nota: "Activo Circulante − Pasivo Circulante",
    },
    {
      etiqueta: "Razón circulante",
      valor: razones.razonCirculante,
      formato: "veces",
      nota: "Activo Circulante / Pasivo Circulante",
    },
    {
      etiqueta: "Prueba del ácido",
      valor: razones.pruebaDelAcido,
      formato: "veces",
      nota: "(Activo Circulante − Inventarios) / Pasivo Circulante",
    },
    {
      etiqueta: "Endeudamiento",
      valor: razones.endeudamiento,
      formato: "porcentaje",
      nota: "Pasivo Total / Activo Total",
    },
    {
      etiqueta: "Apalancamiento",
      valor: razones.apalancamiento,
      formato: "veces",
      nota: "Pasivo Total / Capital Total",
    },
    {
      etiqueta: "Solvencia",
      valor: razones.solvencia,
      formato: "veces",
      nota: "Activo Total / Pasivo Total",
    },
  ];
};
